import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { RouteBroadcast } from '../../models/RouteBroadcast';
import { SignalRService } from '../../services/signalr.service';
import { VersionService } from '../../services/version.service';

@UntilDestroy()
@Component({
    selector: 'signalr-routing-hub',
    templateUrl: './signalr-routing-hub.component.html',
    styleUrls: ['./signalr-routing-hub.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SignalrRoutingHubComponent implements OnDestroy {
  @Input() protectedRoutes: string[];

  connection: any;
  routerSubscription: Subscription;
  routeUsers: RouteBroadcast[] = [];
  routingHubConnection: any;

  constructor(signalRService: SignalRService, private router: Router, private changeDetectorRef: ChangeDetectorRef,
    private versionService: VersionService) {
    this.routingHubConnection = signalRService
      .buildRoutingClient(this.onRoutingBroadcast.bind(this), this.onRoutingLeave.bind(this), this.onVersionChange.bind(this));

    this.routingHubConnection
      .start()
      .then(() => this.onRoutingHubConnected())
      .catch(error => console.error(error.message));
  }

  onRoutingBroadcast(connectionId: string, name: string, userId: string, phoneNumber: string, email: string) {
    if (!this.routeUsers.some(x => x.connectionId === connectionId)) {
      this.routeUsers.push({ connectionId, name, userId, phoneNumber, email });
      this.changeDetectorRef.detectChanges();
    }
  }

  onRoutingLeave(connectionId: string) {
    this.routeUsers = this.routeUsers.filter(x => x.connectionId !== connectionId);
    this.changeDetectorRef.detectChanges();
  }

  onRoutingHubConnected() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    this.routerSubscription = this.router.events.pipe(untilDestroyed(this)).subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.onNavigated();
      }
    });

    this.onNavigated();
  }

  onNavigated() {
    this.routeUsers = [];
    this.changeDetectorRef.detectChanges();
    const route = this.router.url.toString();
    this.routingHubConnection.send('OnNavigate', this.isProtectedPage(route) ? route : '');
  }

  isProtectedPage(page: string) {
    return !!this.protectedRoutes?.some(x => page.indexOf(x) >= 0);
  }

  isRepeatedRouteUserElement(routeUser: RouteBroadcast) {
    return this.routeUsers.find(x => x.userId === routeUser.userId) !== routeUser;
  }

  doesUserExistMoreThanOnce(userId: string) {
    return this.routeUsers.filter(x => x.userId === userId).length > 1;
  }

  onVersionChange(version: string) {
    this.versionService.currentVersion.next(version);
  }

  ngOnDestroy() {
    if (this.connection) {
      this.connection.stop();
    }
  }
}
